import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchAreaViewTilesApi, fetchEmergingAlertHightlightsApi, fetchFuncLocationInfo, fetchSiteRatingsApi } from "../../api/AlertsApi";

type EmergingAlertsStateType = {
    highlightsLoading: boolean | null;
    highlightsLoadingError: string;
    highlights: any[];

    siteRatingsLoading: boolean | null;
    siteRatingsLoadingError: string;
    siteRatings: any[];

    areaViewTilesLoading: boolean | null;
    areaViewTilesLoadingError: string;
    areaViewTiles: any[];

    funcLocInfoLoading: boolean | null;
    funcLocInfoLoadingError: string;
    funcLocInfo: any[];
}

const initialState: EmergingAlertsStateType = {
    highlightsLoading: null,
    highlightsLoadingError: '',
    highlights: [],

    siteRatingsLoading: null,
    siteRatingsLoadingError: '',
    siteRatings: [],

    areaViewTilesLoading: null,
    areaViewTilesLoadingError: '',
    areaViewTiles: [],

    funcLocInfoLoading: null,
    funcLocInfoLoadingError: '',
    funcLocInfo: [],
}

const emergingAlertsSlice = createSlice({
    name: 'emerging_alerts',
    initialState: initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(getEmergingAlertHighlights.pending, state => {
                state.highlightsLoading = true;
            })
            .addCase(getEmergingAlertHighlights.fulfilled, (state, action) => {
                state.highlightsLoading = false;
                state.highlights = [...action.payload]
            })
            .addCase(getEmergingAlertHighlights.rejected, (state) => {
                state.highlightsLoading = false;
                state.highlightsLoadingError = "Unable to fetch alert highlights";
            })

            // emerging alerts site ratings 
            .addCase(getSiteRatings.pending, state => {
                state.siteRatingsLoading = true;
            })
            .addCase(getSiteRatings.fulfilled, (state, action) => {
                state.siteRatingsLoading = false;
                state.siteRatings = [...action.payload]
            })
            .addCase(getSiteRatings.rejected, (state) => {
                state.siteRatingsLoading = false;
                state.siteRatingsLoadingError = "Unable to fetch site ratings";
            })

            // area view tiles
            .addCase(getAreaViewTiles.pending, state => {
                state.areaViewTilesLoading = true;
            })
            .addCase(getAreaViewTiles.fulfilled, (state, action) => {
                state.areaViewTilesLoading = false;
                state.areaViewTiles = [...action.payload]
                state.areaViewTilesLoadingError = '';
            })
            .addCase(getAreaViewTiles.rejected, (state) => {
                state.areaViewTilesLoading = false;
                state.areaViewTilesLoadingError = "Unable to fetch area tiles";
            })

            // area view tiles
            .addCase(getFuncLocInfo.pending, state => {
                state.funcLocInfoLoading = true;
            })
            .addCase(getFuncLocInfo.fulfilled, (state, action) => {
                state.funcLocInfoLoading = false;
                state.funcLocInfo = [...action.payload]
                state.funcLocInfoLoadingError = '';
            })
            .addCase(getFuncLocInfo.rejected, (state) => {
                state.funcLocInfoLoading = false;
                state.funcLocInfoLoadingError = "Unable to fetch functional location details";
            })
    }
})

export const getEmergingAlertHighlights = createAsyncThunk(
    'emerging_alerts/getEmergingAlertHighlights',
    async () => {
        return await fetchEmergingAlertHightlightsApi();
    }
)

export const getSiteRatings = createAsyncThunk(
    'emerging_alerts/getSiteRatings',
    async () => {
        return await fetchSiteRatingsApi();
    }
)

export const getAreaViewTiles = createAsyncThunk(
    'emerging_alerts/getAreaViewTiles',
    async () => {
        return await fetchAreaViewTilesApi();
    }
)

export const getFuncLocInfo = createAsyncThunk(
    'emerging_alerts/getFuncLocInfo',
    async () => {
        return await fetchFuncLocationInfo();
    }
)

export default emergingAlertsSlice.reducer;

export const selectIfAlertHighlightsLoading = (state: any) => state.emerging_alerts.highlightsLoading;
export const selectIfAlertHighlightsLoadingError = (state: any) => state.emerging_alerts.highlightsLoadingError;
export const selectEmergingAlertHighlights = (state: any) => state.emerging_alerts.highlights;

export const selectIfSiteRatingsLoading = (state: any) => state.emerging_alerts.siteRatingsLoading;
export const selectIfSiteRatingsLoadingError = (state: any) => state.emerging_alerts.siteRatingsLoadingError;
export const selectSiteRatings = (state: any) => state.emerging_alerts.siteRatings;
export const selectSiteRatingByArea = (area: string) => (state: any) => state.emerging_alerts.siteRatings.find((s: any) => s.site_name === area);

export const selectIfAreaTilesLoading = (state: any) => state.emerging_alerts.areaViewTilesLoading;
export const selectIfAreaTilesLoadingError = (state: any) => state.emerging_alerts.areaViewTilesLoadingError;
export const selectAreaViewTiles = (area: string) => (state: any) => state.emerging_alerts.areaViewTiles.filter((s: any) => s.plant_name.toLowerCase() === area.toLowerCase());

export const selectIfFuncLocInfoLoading = (state: any) => state.emerging_alerts.funcLocInfoLoading;
export const selectIfFuncLocInfoLoadingError = (state: any) => state.emerging_alerts.funcLocInfoLoadingError;
export const selectIfFuncLocInfo = (funcLoc: string) => (state: any) => {
    return state.emerging_alerts.funcLocInfo
    .filter((s: any) =>s.area_code !== null && s.area_code.toLowerCase() === funcLoc.toLowerCase())
}